import shortid from 'shortid';
import dot from 'dot-object';

import ServiceAccountKey from '../ServiceAccountKey';
import sheetConstants from '../data/sheetConstants';

const NUMBER_OF_VENDORS_TO_BOOTSTRAP = 1000;

const publicUrl = process.env.PUBLIC_URL || 'http://localhost:3000';

const stringToUserEnteredValue = (stringValue) => (
    {
        userEnteredValue: {
            stringValue
        }
    });

const createVendorSheetStructure = (vendorIds) => {
    const headerRow = {
        rowData: [
            {
                values: [
                    'Id',
                    'Name',
                    'Point of Contact',
                    'Department',
                    'Expected Reservation Count',
                    'RSVP Link',
                    'Summary Link'
                ].map(stringToUserEnteredValue)
            }
        ],
        startRow: 0
    };

    const vendorDataRows = vendorIds.map((id, index) => ({
        rowData: [
            {
                values: [
                    id
                ].map(stringToUserEnteredValue)
            }
        ],
        rowMetadata: [
            {
                developerMetadata: [
                    {
                        metadataKey: 'id',
                        metadataValue: id,
                        visibility: 'DOCUMENT'
                    }
                ]
            }
        ],
        startRow: index + 1
    }));

    return {
        data: [headerRow, ...vendorDataRows],
        properties: {
            sheetId: sheetConstants.vendorSheetId,
            title: sheetConstants.vendorSheetName
        }
    };
};

const settingsStructure = async (template) => ({
    data: [
        {
            rowData: Object.entries(dot.dot(await import(`./${template}.json`)))
                .map((entries) => ({
                    values: entries.map(stringToUserEnteredValue)
                })),
            startRow: 0
        }
    ],
    properties: {
        sheetId: sheetConstants.settingsSheetId,
        title: sheetConstants.settingsSheetName
    }
});

const reservationStructure = {
    data: [
        {
            rowData: [
                {
                    values: [
                        'organization',
                        'id',
                        'attendeeId',
                        'firstName',
                        'lastName',
                        'textField1',
                        'address',
                        'city',
                        'state',
                        'zip',
                        'email',
                        'phone',
                        'textField2'
                    ].map(stringToUserEnteredValue)
                }
            ],
            startRow: 0
        }
    ],
    properties: {
        sheetId: sheetConstants.reservationsSheetId,
        title: sheetConstants.reservationsSheetName
    }
};

const createSheet = async ({accessToken, title, vendorIds, template}) => {
    const payload = {
        properties: {
            title
        },
        sheets: [
            createVendorSheetStructure(vendorIds),
            reservationStructure,
            await settingsStructure(template)
        ]
    };

    const response = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
        body: JSON.stringify(payload),
        headers: {
            Authorization: `Bearer ${accessToken}`
        },
        method: 'POST'
    });

    if (!response.ok) {
        throw new Error(response.statusText);
    }

    return response.json();
};

const protectSheet = async (spreadsheetId, accessToken) => {
    const payload = {
        requests: {
            addProtectedRange: {
                protectedRange: {
                    range: {
                        endColumnIndex: 6,
                        sheetId: sheetConstants.vendorSheetId,
                        startColumnIndex: 5
                    },
                    warningOnly: true
                }
            }
        }
    };

    const response = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`, {
        body: JSON.stringify(payload),
        headers: {
            Authorization: `Bearer ${accessToken}`
        },
        method: 'POST'
    });

    if (!response.ok) {
        throw new Error(response.statusText);
    }
};

const populateVendorRows = async (spreadsheetId, accessToken, vendorIds) => {
    const payload = {
        data: [
            {
                range: 'B2:H1002',
                values: vendorIds
                    .map((vendorId) => [
                        '', // name
                        '', // hy-vee contact
                        '', // department
                        0, // golf spots
                        `=HYPERLINK("${publicUrl}/event/${spreadsheetId}/${vendorId}")`,
                        `=HYPERLINK("${publicUrl}/event/${spreadsheetId}/${vendorId}/summary")`
                    ])
            }
        ],
        valueInputOption: 'USER_ENTERED'
    };

    const response = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values:batchUpdate`, {
        body: JSON.stringify(payload),
        headers: {
            Authorization: `Bearer ${accessToken}`
        },
        method: 'POST'
    });

    if (!response.ok) {
        throw new Error(response.statusText);
    }
};

const grantAccessToServiceAccount = async (spreadsheetId, accessToken) => {
    const payload = {
        emailAddress: ServiceAccountKey.client_email,
        role: 'writer',
        type: 'user'
    };

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${spreadsheetId}/permissions?sendNotificationEmail=false`, {
        body: JSON.stringify(payload),
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-type': 'application/json'
        },
        method: 'POST'
    });

    if (!response.ok) {
        throw new Error(response.statusText);
    }
};

const generateVendorIds = () => [...new Array(NUMBER_OF_VENDORS_TO_BOOTSTRAP)]
    .map(() => shortid.generate());

export default async ({accessToken, fileName, template}) => {
    const vendorIds = generateVendorIds();
    const sheet = await createSheet({
        accessToken,
        title: fileName,
        vendorIds,
        template
    });

    await protectSheet(sheet.spreadsheetId, accessToken);
    await populateVendorRows(sheet.spreadsheetId, accessToken, vendorIds);
    await grantAccessToServiceAccount(sheet.spreadsheetId, accessToken);

    return {
        url: sheet.spreadsheetUrl
    };
};



// WEBPACK FOOTER //
// ./src/admin/BootstrapGoogleSheet.js